import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-5 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="mb-10 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#00E676]">
            <Zap className="h-5 w-5 text-black" fill="black" />
          </span>
          <span className="font-display text-xl font-black tracking-tight">TipNu</span>
        </Link>

        <h1 className="font-display text-4xl font-black tracking-tight">Get your tip link.</h1>
        <p className="mt-2 text-zinc-400">Let anyone tip you with just your username.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Full name</Label>
            <Input
              data-testid="register-name-input"
              value={form.name}
              onChange={upd("name")}
              required
              className="mt-2 h-12 border-white/10 bg-[#0F0F0F] text-white focus-visible:ring-[#00E676]"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Username</Label>
            <Input
              data-testid="register-username-input"
              value={form.username}
              onChange={upd("username")}
              required
              placeholder="marcusv"
              className="mt-2 h-12 border-white/10 bg-[#0F0F0F] text-white focus-visible:ring-[#00E676]"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Email</Label>
            <Input
              data-testid="register-email-input"
              type="email"
              value={form.email}
              onChange={upd("email")}
              required
              className="mt-2 h-12 border-white/10 bg-[#0F0F0F] text-white focus-visible:ring-[#00E676]"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Password</Label>
            <Input
              data-testid="register-password-input"
              type="password"
              value={form.password}
              onChange={upd("password")}
              required
              className="mt-2 h-12 border-white/10 bg-[#0F0F0F] text-white focus-visible:ring-[#00E676]"
            />
          </div>

          {error && (
            <p data-testid="register-error" className="text-sm text-red-400">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="register-submit-btn"
            disabled={loading}
            className="flex h-12 w-full items-center justify-center rounded-full bg-[#00E676] font-bold text-black transition-transform active:scale-95 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create tip link"}
          </button>
        </form>

        <p className="mt-6 text-sm text-zinc-500">
          Already have one?{" "}
          <Link to="/login" data-testid="register-to-login" className="font-semibold text-[#00E676]">
            Log in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
